import { useSyncExternalStore } from 'react';
import AuthService from '../services/auth.service';

const authService = AuthService.getInstance();

/**
 * React-reactive access to the current access token.
 *
 * Useful as an `enabled` gate for `useQuery` hooks that must wait for auth to
 * arrive before firing — without it, FormatLocaleProvider and other top-of-tree
 * providers fire queries before auth is ready, producing spurious 401s.
 *
 * MUST NOT DEPEND ON THE AUTH PROVIDER
 * ------------------------------------
 * FormatLocaleProvider wraps the router in main.tsx, which puts it OUTSIDE the
 * FronteggProvider that <App/> renders. Consumers up there (useTenantConfig ->
 * FormatLocaleContext) call this hook with no provider mounted, so reading
 * provider state here throws "Cannot read properties of null (reading 'store')"
 * and takes down the whole app. The singleton is the only thing safe to read.
 *
 * Components INSIDE the provider that need to re-render the moment auth lands
 * should use `useAppAuth()` directly — this hook cannot do that for them.
 *
 * Consumers re-render when callers invoke `authService.notifyAuthChanged()`
 * (typically from the embed / OIDC handoff paths after hydrating the
 * singleton).
 */
export function useAuthToken(): string | null {
  return useSyncExternalStore(
    (cb) => authService.subscribe(cb),
    () => authService.getAuthTokenSnapshot(),
    () => authService.getAuthTokenSnapshot(),
  );
}
